import React, { useCallback, useState } from 'react';
import useScript from '../../../../_hooks/useScript';
import { FullPageLoading } from '../../../../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { ErrorState } from '../../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { useOrionRiskIntelligenceWorkflowQuery } from './api/useOrionRiskIntelligenceWorkflowQuery';
import {
  OrionRiskIntelligenceWorkflowSendSurveyResponse,
  useOrionRiskIntelligenceWorkflowMutation,
} from './api/useOrionRiskIntelligenceWorkflowMutation';
import { toast } from '../../../../CommonComponents/OPL/Toast/Toast';
import { Results } from '../HiddenLeversRTQ/components/Results/Results';
import { HiddenLeversRTQ } from '../HiddenLeversRTQ/HiddenLeversRTQ';

interface Props {
  onFinish?(): void;
}

export const OrionRiskIntelligenceQuestionnaire = ({ onFinish }: Props) => {
  const { data, isLoading, error } = useOrionRiskIntelligenceWorkflowQuery();

  const [surveyDetails, setSurveyDetails] = useState<
    OrionRiskIntelligenceWorkflowSendSurveyResponse['detail'] | null
  >(null);

  const [isScriptLoaded, scriptError] = useScript(
    `${ADV.settings.endpoints.assets}js/oat_risk_tolerance_wrapper_questionaire.js`,
  );

  const {
    mutateAsync: saveSurvey,
    isLoading: isSaving,
    isError: errorSaving,
    data: portfolio,
  } = useOrionRiskIntelligenceWorkflowMutation();

  const handleEvents = useCallback(
    element => {
      if (element) {
        element.addEventListener(
          'sendSurvey',
          async (response: OrionRiskIntelligenceWorkflowSendSurveyResponse) => {
            const { detail } = response;

            setSurveyDetails(detail);

            try {
              await saveSurvey(detail);
              if (onFinish) {
                onFinish();
              }
            } catch (err) {
              console.error(err);
              if (err instanceof Error) {
                toast.error(err.message);
              }
            }
          },
        );
      }
    },
    [onFinish, saveSurvey],
  );

  if (!isScriptLoaded || isLoading || isSaving) {
    return <FullPageLoading text="Loading" />;
  }

  if (scriptError && error) {
    return (
      <ErrorState
        height="100%"
        message="There was an error loading the questioner. Please try again later."
      />
    );
  }

  if (errorSaving && surveyDetails) {
    return (
      <ErrorState
        height="100%"
        onAction={async () => {
          await saveSurvey(surveyDetails);

          if (onFinish) {
            onFinish();
          }
        }}
      />
    );
  }

  if (portfolio && surveyDetails) {
    return <Results portfolio={portfolio} onRestart={() => setSurveyDetails(null)} />;
  }

  if (!data) {
    return null;
  }

  const { environment, surveyId } = data;

  return (
    <>
      <h1 className="sr-only">Risk Tolerance Questionnaire</h1>
      <oat-rtq-wrapper ref={handleEvents} survey-id={surveyId} environment={environment} />
    </>
  );
};
